<template>
  <div>
    <div class="mb-20" id="welcome-settings">
      <div class="text-2xl font-bold">
        {{ $t('frontend_groovemail_forms_step3_welcome') }}
      </div>
      <div class="text-sm mt-2 mb-4 text-grey-700">
        {{ $t('frontend_groovemail_forms_step3_configure') }}
      </div>
      <div class="form-content-box p-10 pl-24 mb-4 relative">
        <div class="absolute" style="left: 40px; top: 48px">
          <MailIcon />
        </div>
        <gd-custom-radio
          v-model="localModel.welcome_msg_option"
          class="form-setup-radio"
          :option="options[0]"
          value="localModel.welcome_msg_option"
          @onUpdateOption="welcomeMessageOptionUpdated"
        ></gd-custom-radio>
        <div v-if="localModel.welcome_msg_option === 'email'">
          <div class="mt-10" style="width: 400px">
            <div class="mb-6">
              <div class="text-base font-semibold mb-2">
                {{ $t('frontend_groovemail_forms_step3_sender') }}
              </div>
              <gd-dropdown
                size="full"
                variant="base"
                :placeholder="$t('frontend_groovemail_sequences_email_select_sender')"
                v-model="localModel.welcome_sender_id"
                :options="emailSenderList"
                @input="updateEmailSender"
              />
              <div
                class="text-sm text-danger"
                v-if="
                  this.isEmailContentSubmitted &&
                  this.localModel.welcome_msg_option === 'email' &&
                  (!this.localModel.welcome_sender_id ||
                    this.localModel.welcome_sender_id === '-1')
                "
              >
                {{ $t('frontend_groovemail_campaigns_create_select_sender_to_proceed') }}
              </div>
              <div
                class="flex justify-end text-primary text-sm font-semibold cursor-pointer"
                @click="openCreateEmailSenderModal"
              >
                <PlusIcon class="h-5 w-5"/>
                <p>{{ $t('frontend_groovemail_settings_integeration_add_new_integeration') }}</p>
              </div>
            </div>
            <div class="mb-6">
              <div class="text-base font-semibold mb-2 flex items-center">
                <span class="pr-1">{{
                  $t('frontend_groovemail_forms_step3_subject')
                }}</span>
                <button
                  v-tooltip="{
                    content: $t('frontend_add_dynamic_variables_to_subject'),
                    placement: 'right-start',
                    classes: ['info'],
                    targetClasses: ['it-has-a-tooltip'],
                    offset: 10,
                    delay: {
                      show: 200,
                      hide: 300,
                    },
                  }"
                >
                  <InfoIcon class="text-grey-600 h-4 w-4"/>
                </button>
              </div>
              <div class="relative">
                <text-input
                  variant="base"
                  v-model="localModel.welcome_msg_subject"
                  :placeholder="$t('frontend_groovemail_forms_step3_welcome_message_subject')"
                  :validator="$v.localModel.welcome_msg_subject"
                />
                <div
                  v-click-outside="closeSubjectVariables"
                  class="plus-round-icon bg-primary absolute"
                  style="right: 8px; top: 8px"
                  @click="openSubjectVariables"
                >
                  <div
                    class="bg-white btn-radius py-6 pl-6 absolute z-10 overflow-auto"
                    style="
                      width: 290px;
                      right: 0;
                      top: 39px;
                      box-shadow: 20px 20px 40px 0 rgba(22, 29, 37, 0.05);
                    "
                    v-if="showSubjectDropdown"
                  >
                    <div class="pr-6">
                      <text-input
                        variant="base"
                        v-model="emailSubjectVariableSearch"
                        @input="searchQueryChangedSmsVariable"
                        :placeholder="$t('frontend_groovemail_tables_search_query')"
                      >
                        <SearchIcon slot="prependIcon"/>
                      </text-input>
                    </div>
                    <VuePerfectScrollbar>
                      <div
                        class="mt-6 text-sm font-semibold"
                        style="max-height: 300px"
                      >
                        <div
                          class="mb-6"
                          v-for="(
                            option, index
                          ) in dynamic_variables_sms_content_subject"
                          :key="index"
                        >
                          <div
                            class="cursor-pointer text-grey-700 text-left"
                            @click="copyVariableInSubject(option)"
                          >
                            {{ option.label }}
                          </div>
                        </div>
                      </div>
                    </VuePerfectScrollbar>
                  </div>
                </div>
              </div>
            </div>

            <div class="mb-6">
              <div class="text-base font-semibold mb-2">
                {{ $t('frontend_groovemail_forms_step3_preview') }}
              </div>
              <text-input
                variant="base"
                v-model="localModel.welcome_msg_previewtext"
                :placeholder="$t('frontend_groovemail_campaigns_enter_preview')"
              />
            </div>
          </div>

          <div class="flex flex-col my-6">
            <label class="font-semibold text-base mb-2">
              {{ $t('frontend_groovemail_email_types_label') }}
            </label>
            <div class="flex">
              <div
                v-for="(option, index) in email_type_options"
                :key="index"
                class="w-1/3 campaign-radio"
                :class="{ 'pl-2': index !== 0, 'pr-2': index !== email_type_options.length - 1 }"
              >
                <gd-custom-radio
                  v-model="localModel.welcome_editor_type"
                  class="bg-white border p-8 pb-2 text-center"
                  :option="option"
                  value="localModel.welcome_editor_type"
                ></gd-custom-radio>
              </div>
            </div>
          </div>

          <div class="w-full">
            <div v-if="localModel.welcome_editor_type === 3 && showEmailComposers">
              <div class="mb-2">
                <label class="font-semibold text-base">
                  {{ $t('frontend_groovemail_campain_message') }}
                </label>
              </div>
              <div class="mb-2" style="width: 400px" v-if="this.localModel.welcome_template_name">
                <label class="font-semibold text-base">
                  {{ $t('frontend_groovemail_email_builder_past_template_email_name') }} : {{ this.localModel.welcome_template_name }}
                </label>
              </div>
              <div class="flex justify-between" style="width: 400px">
                <gd-button
                  size="expand"
                  variant="primary"
                  @click="openEmailBuilder"
                  >{{
                    this.localModel.welcome_email_id
                    ? $t('frontend_groovemail_email_builder_edit_your_email_placeholder')
                    : $t('frontend_groovemail_email_builder_compose_your_email')
                  }}</gd-button
                >
                <gd-button
                  size="md"
                  class="ml-4"
                  :class="{
                    'cursor-not-allowed disabled-preview': !localModel.welcome_msg_content,
                  }"
                  @click="
                    localModel.welcome_msg_content ? openWelcomePreview() : null
                  "
                >
                  <PreviewIcon class="h-5 w-5"/>
                </gd-button>
              </div>
            </div>
            <div v-if="localModel.welcome_editor_type !== 3 && showEmailComposers">
              <email-basic-editor
                :key="2"
                ref="welcome-form-email-editor"
                :editorOptions="welcomeFormEditorOptions"
                :editorContent="localModel.welcome_msg_content"
                :editorNodeWithNoMargin="true"
                :editorWithBlockedLinkRestriction="true"
                :editorDynamicVariables="builderVariables"
                openedFrom="welcome-forms"
                :allowEditorPreview="true"
                :allowEditorHtmlView="false"
                :currentEditorView="localModel.welcome_editor_type"
                @editorContentUpdated="localModel.welcome_msg_content = $event"
                @templateSelected="localModel.welcome_msg_subject = $event.subject; localModel.welcome_msg_previewtext = $event.previewText"
              ></email-basic-editor>
            </div>

            <div
              class="text-sm text-danger"
              v-if="checkValidEmailContent()"
            >
              {{ $t('frontend_groovemail_forms_enter_welcome_email_content_to_proceed') }}
            </div>
          </div>
          <div class="mt-4">
            <SendTestEmail
              ref="sendWelcomeTestMail"
              @showLoader="showLoader"
              @hideLoader="hideLoader"
              @proceeedSendingTestEmail="proceeedSendingTestEmail"
            ></SendTestEmail>
          </div>
        </div>
      </div>
      <div class="form-content-box p-10 pl-24 mb-4 relative">
        <div class="absolute" style="left: 40px; top: 48px">
          <MessageIcon />
        </div>
        <gd-custom-radio
          v-model="localModel.welcome_msg_option"
          class="form-setup-radio"
          :option="options[1]"
          value="localModel.welcome_msg_option"
          @onUpdateOption="welcomeMessageOptionUpdated"
        ></gd-custom-radio>
        <div v-if="localModel.welcome_msg_option === 'sms'">
          <div class="mt-10">
            <div class="mb-6" style="width: 400px">
              <div class="text-base font-semibold mb-2">
                {{ $t('frontend_groovemail_campaigns_sms_sender_label') }}
              </div>
              <gd-dropdown
                size="full"
                variant="base"
                :placeholder="$t('frontend_groovemail_sequences_email_select_sender')"
                v-model="localModel.welcome_sender_id"
                :options="smsSenderList"
                @input="updateSmsSender"
              />

              <div
                class="text-sm text-danger"
                v-if="
                  this.isSmsContentSubmitted &&
                  this.localModel.welcome_msg_option === 'sms' &&
                  (!this.localModel.welcome_sender_id ||
                    this.localModel.welcome_sender_id === '-1')
                "
              >
                {{ $t('frontend_groovemail_campaigns_create_select_sender_to_proceed') }}
              </div>

              <div
                class="flex justify-end text-primary text-sm font-semibold cursor-pointer"
                @click="openCreateSmsSenderModal()"
              >
                <PlusIcon class="h-5 w-5"/>
                <p>{{ $t('frontend_groovemail_settings_integeration_add_new_integeration') }}</p>
              </div>

            </div>
            <div class="flex justify-between mb-2">
              <div class="font-semibold text-base">
                {{ $t('frontend_groovemail_campains_step4_send_message') }}
              </div>
              <div class="flex relative">
                <div class="text-sm text-grey-700 mr-2">
                  {{ $t('frontend_groovemail_campaigns_wizard_broadcasts_campaign_insert_varaibles') }}
                </div>
                <div
                  v-click-outside="closeSmsVariables"
                  class="plus-round-icon bg-primary"
                  @click="openSmsVariables"
                >
                  <div
                    class="bg-white btn-radius py-6 pl-6 absolute z-10 overflow-auto"
                    style="
                      width: 290px;
                      right: 0;
                      top: 30px;
                      box-shadow: 20px 20px 40px 0 rgba(22, 29, 37, 0.05);
                    "
                    v-if="showSmsVariableDropdown"
                  >
                    <div class="pr-6">
                      <text-input
                        variant="base"
                        v-model="smsVariableSearch"
                        @input="searchQueryChangedSmsVariable"
                        :placeholder="$t('frontend_groovemail_tables_search_query')"
                      >
                        <SearchIcon slot="prependIcon"/>
                      </text-input>
                    </div>
                    <VuePerfectScrollbar>
                      <div
                        class="mt-6 text-sm font-semibold"
                        style="max-height: 300px"
                      >
                        <div
                          class="mb-6"
                          v-for="(
                            option, index
                          ) in dynamic_variables_sms_content_subject"
                          :key="index"
                        >
                          <div
                            class="cursor-pointer text-grey-700 text-left"
                            @click="copySmsvariable(option)"
                          >
                            {{ option.label }}
                          </div>
                        </div>
                      </div>
                    </VuePerfectScrollbar>
                  </div>
                </div>
              </div>
            </div>

            <text-area
              variant="base"
              id="sms-text-area"
              style="height: 260px"
              v-model="localModel.welcome_msg_sms_content"
              :placeholder="$t('frontend_groovemail_campaigns_create_broadcast_enter_sms_content')"
              :validator="$v.localModel.welcome_msg_sms_content"
            />
            <div
              class="text-sm text-danger"
              v-if="
                isSmsContentSubmitted &&
                !this.localModel.welcome_msg_sms_content
              "
            >
              {{ $t('frontend_groovemail_forms_enter_welcome_sms_content_to_proceed') }}
            </div>
          </div>
        </div>
      </div>

      <div class="form-content-box p-10 pl-24 mb-4 relative">
        <div class="absolute" style="left: 40px; top: 48px">
          <CrossIcon class="h-10 w-10 text-grey-600"/>
        </div>
        <gd-custom-radio
          v-model="localModel.welcome_msg_option"
          class="form-setup-radio"
          :option="options[2]"
          value="localModel.welcome_msg_option"
          @onUpdateOption="welcomeMessageOptionUpdated"
        ></gd-custom-radio>
      </div>
    </div>

    <div class="mb-20" id="recipients">
      <div class="text-2xl font-bold">
        {{ $t('frontend_groovemail_forms_step3_forward_responses') }}
      </div>
      <div class="text-sm mt-2 mb-6 text-grey-700">
        {{ $t('frontend_groovemail_forms_step3_forward_responses_desc') }}
      </div>
      <div class="form-content-box p-10">
        <div class="flex justify-between">
          <div class="font-semibold mb-6">
            {{ $t('frontend_groovemail_forms_step3_select_main_account_email_heading') }}
          </div>
          <div>
            <gd-toggle
              bgClass="bg-grey-300"
              v-model="localModel.default_acc_email"
            />
          </div>
        </div>

        <div class="font-semibold mb-2">
          {{ $t('frontend_groovemail_forms_step3_select_recipients_heading') }}
        </div>
        <div style="width: 600px" class="flex mt-2">
          <div>
            <treeselect
              :key="3"
              class="custom-treeselect treeselect-add-new treeselect-width-search"
              v-model="localModel.recipients"
              :closeOnSelect="closeOnSelect"
              :clearable="clearable"
              :clearOnSelect="clearOnSelect"
              :multiple="true"
              :valueFormat="'object'"
              :options="recipientData"
              :normalizer="normalizer"
              :placeholder="this.$t('frontend_groovemail_forms_step3_select_recipients')"
              @select="recipientSelected"
              @deselect="recipientRemoved"
            />
          </div>
          <div class="my-auto mx-2">
            <div v-if="disableRefreshRecipientIcon">
              <span class="tooltip-refresh-btn">
                <RefreshIcon class="h-5 w-5 mt-1 ml-2 refresh-icon-disabled" />
                <span class="tooltiptext">{{$t('frontend_groovemail_disabled_refresh_recipient_button_tooltip')}}</span>
              </span>
            </div>
            <div v-else  @click="refreshRecipients()">
              <span class="tooltip-refresh-btn">
                <RefreshIcon class="h-5 w-5 mt-1 ml-2 refresh-icon text-primary" id="refreshIcon-recipients" />
                <span class="tooltiptext">{{$t('frontend_groovemail_refresh_recipient_button')}}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Email builder modal -->
    <slide-over
      id="generic-email-builder-modal"
      bgColor="white"
      width="full"
      animation="slideup"
      name="generic-email-builder-modal-welcome"
      @collapse="openEmailChangeLostConfirm('email')"
    >
      <email-builder
        ref="welcome-form-email-builder"
        :emailTemplateId="emailTemplateId"
        :templateId="welcome_template_id"
        :emailMeta="getEmailMetaData"
        :openedFrom="'welcome-form'"
        :openedType="localModel.welcome_email_id ? 'edit' : 'add'"
        :templateType="templateType"
        :builderVariables="builderVariables"
        :blockedLinkRestriction="true"
        :showResetTemplate="true"
        :allowSaveRows="true"
        @saveEmailTemplateContent="saveContentWithMeta"
        @changeCurrentTemplate="openEmailChangeLostConfirm('template')"
        @showLoader="showLoader"
        @hideLoader="hideLoader"
      ></email-builder>
    </slide-over>

    <gd-modal
      id="add-recipient-modal"
      name="add-recipient-modal"
      :title="$t('frontend_groovemail_settings_recipients_add_new_recipient')"      
      @closed="addRecipientModalClosed()"
    >
      <AddNewRecipient 
        ref="addNewRecipientModal"
        modalType="add"
        :validator="$v.newRecipientData"
        :model="newRecipientData"
        :urls="urls"
        @showLoader="showLoader"
        @hideLoader="hideLoader"
        @showErrorMessage="requestErrorAlert"
        @showSuccessMessage="showSuccessMessage"
        @closeRecipientModal="closeRecipientModal"
      />
    </gd-modal>

    <!-- Confirmation email change lost modal -->
    <confirm-modal
      :ref="`step3-confirmation-email-change-lost`"
      :name="`step3-confirmation-email-change-lost`"
      :buttonText="$t('frontend_confirm')"
      :cancelText="$t('frontend_cancel')"
      :hideClose="true"
      :confirmMessage="this.$t('frontend_bouses_delete_bonus_title')"
      :messageContent="confirmMessage"
      @confirm="emailChangeLostConfirm"
    />


    <!-- Confirmation editor type change modal -->
    <confirm-modal
      :ref="`step3-confirmation-editor-type`"
      :name="`step3-confirmation-editor-type`"
      :buttonText="$t('frontend_confirm')"
      :cancelText="$t('frontend_cancel')"
      :hideClose="true"
      :confirmMessage="this.$t('frontend_bouses_delete_bonus_title')"
      :messageContent="this.$t('frontend_groovemail_email_type_change_warning')"
      @confirm="confirmEditorTypeChange"
      @close="closeConfirmEditorTypeChange"
    />
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import {
  required,
  minLength,
  maxLength,
  email,
  numeric,
} from 'vuelidate/lib/validators';
import Notify from '@groovepages/gd-ui-kit/notify';

import VuePerfectScrollbar from 'vue-perfect-scrollbar';
import ClickOutside from 'vue-click-outside';
import Treeselect from '@riophae/vue-treeselect';
import themeConfig from '@/theme/themeConfig';
import AddNewRecipient from '@/components/AddNewRecipient.vue';
import EmailBuilder from '@/components/befree-email-builder/builder.vue';
import EmailBasicEditor from '@/components/EmailBasicEditor.vue';
import { syncEditorContent } from '@/components/emailContentValidation';
import SlideOver from '@/components/SlideOver.vue';
import SendTestEmail from '@/components/send-test-email';
import showAlert from '@/components/sweet-alert-popup.js';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import { removeSpinningClass, addSpinningClass } from '@/utils/refreshHelper';
import { showSuccessDialog } from '@/components/showDialog.js';
import { sendersUrl } from '@/utils/urls.js';

// import icons
import InfoIcon from '@/assets/images/icons/new/info_new.svg';
import PreviewIcon from '@/assets/images/icons/new/preview_new.svg';
import CrossIcon from '@/assets/images/icons/new/cross_new.svg';
import PlusIcon from '@/assets/images/icons/new/plus-small_new.svg';
import RefreshIcon from '@/assets/images/icons/new/refresh_new.svg';
import MailIcon from '@/assets/images/icons/Camp-Email.svg';
import MessageIcon from '@/assets/images/icons/Camp-Sms.svg';
import SearchIcon from '@/assets/images/icons/new/search_new.svg';

export default {
  name: 'Step3',
  urls: {},
  components: {
    EmailBuilder,
    VuePerfectScrollbar,
    InfoIcon,
    MailIcon,
    MessageIcon,
    CrossIcon,
    SlideOver,
    Treeselect,
    EmailBasicEditor,
    SendTestEmail,
    AddNewRecipient,
    PreviewIcon,
    PlusIcon,
    RefreshIcon,
    SearchIcon,
  },

  directives: {
    ClickOutside,
  },

  props: {
    model: {
      required: true,
    },
  },

  data() {
    return {
      newRecipientData: {
        name:'',
        email: '',
        otp: '',
      },
      refreshTimeoutId: null,
      disableRefreshRecipientIcon: false,
      editorTypeChangedInternally: false,
      showEmailComposers: true,
      dynamic_variables_sms_content_subject: [],
      smsVariableSearch: '',
      emailSubjectVariableSearch: '',
      clearable: false,
      closeOnSelect: true,
      clearOnSelect: true,
      showSubjectDropdown: false,
      showSmsVariableDropdown: false,
      isSmsContentSubmitted: false,
      isEmailContentSubmitted: false,
      formConatinsPhone: false,
      verifiedSendersRequired: false,
      localModel: this.model,
      senders: [],
      smsSenders: [],
      recipients: [],
      normalizer(node) {
        return {
          id: node.id,
          label: node.email,
        };
      },
      welcomeFormEditorOptions: {
        modules: {
          toolbar: {
            container: [
              ['bold', 'italic', 'underline', 'strike'], // toggled buttons
              [{ list: 'ordered' }, { list: 'bullet' }],

              ['link', 'image'],
              [{ size: ['10px', false, '18px', '32px'] }], // custom dropdown
              [{ header: [1, 2, 3, 4, 5, 6, false] }],

              [{ color: [] }, { background: [] }], // dropdown with defaults from theme
              [{ align: [] }],
            ],
            handlers: {
              image: () => this.handleImageInsertionInContent()
            }
          },
        },
      },
      emailTemplateId: '',
      templateType: '',
      welcome_template_id: null,
      newEmailType: null,
      oldEmailType: null,
      confirmMessage: '',
      confirmTarget: '',
    };
  },

  async created() {
    this.showLoader();
    try {
      this.urls = {
        recipient: `${sendersUrl.recipient}`
      };
      await this.checkPhonePresent();
      await this.fetchSendersStatus();
      await this.fetchSendersData();
      await this.fetchCustomFieldVariable();
      if (!this.localModel.welcome_msg_content) {
        this.localModel.welcome_msg_content = '';
      }
      this.dynamic_variables_sms_content_subject = this.dynamic_variables_sms_subject;
      await this.fetchRecipients();
      if (this.localModel.welcome_msg_option === 'email' && this.localModel.welcome_email_id) {
        this.emailTemplateId = this.localModel.welcome_email_id;
        this.templateType = 'user-email';
      }
      this.hideLoader();

    } catch (error) {
      this.requestErrorAlert(error.response?.data?.error);
    }
  },

  validations: {
    localModel: {
      welcome_msg_subject: {
        required,
      },
    },
    newRecipientData: {
      name: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(50),
      },
      email: {
        required,
        email,
        minLength: minLength(3),
        maxLength: maxLength(50),
      },
      otp: {
        required,
        numeric,
        minLength: minLength(4),
        maxLength: maxLength(4),
      },
    },
  },
  

  methods: {
    ...mapGetters('mailing', ['getCanMail']),

    showLoader() {
      this.$emit('showLoader');
    },

    hideLoader() {
      this.$emit('hideLoader');
    },

    // error message for the request field
    async requestErrorAlert(error) {
      // hide the loader
      this.hideLoader();
      
      await showAlert({
        iconType: 'alert-error',
        html : `
          <style>.swal2-cancel { color: ${themeConfig.appPrimaryColorInvert} !important; }</style>
          <p class="text-sm text-deep-dark mb-3">${error || this.$t('frontend_backend_error') }</p>
        `,
        showCancelButton: false,
        showCloseButton: true,
        confirmButtonColor: themeConfig.appPrimaryColor,
        confirmButtonText: this.$t('frontend_close'),
      });
    },

    handleImageInsertionInContent() {
      this.$refs['welcome-form-email-editor'].handleImageInsertionInContent(); 
    },

    checkValidEmailContent() {
      let validEmailContent = false;
      if (this.isEmailContentSubmitted) {
        if (this.localModel.welcome_editor_type === 1 || this.localModel.welcome_editor_type === 2) {
          if (this.localModel.welcome_msg_content) {
            validEmailContent = true;
          }
        } else if (
          this.localModel.welcome_msg_content ||
          this.localModel.welcome_email_sections
        ) {
          validEmailContent = true;
        }
      } else {
        validEmailContent = true;
      }
      return !validEmailContent;
    },

    validateEmailContent() {
      const editor = this.$refs ? this.$refs['welcome-form-email-editor'] : null;
      syncEditorContent(editor, (html) => {
        this.localModel.welcome_msg_content = html;
      });
    },

    async welcomeMessageOptionUpdated() {
      this.localModel.welcome_editor_type = 3;
      this.localModel.welcome_email_id = null;
      this.localModel.welcome_template_name = '';
      this.localModel.welcome_template_folder = null;
      await this.fetchSendersData();
    },

    // open emailChangeLostConfirm modal
    openEmailChangeLostConfirm(option) {
      // set the confirmTarget
      this.confirmTarget = option;

      if (option === 'email') {
        // set the confirmMessage and confirmTarget
        this.confirmMessage = this.$t('frontend_groovemail_email_builder_content_will_be_lost');

        // check change in content of email builder
        const changeInContent = this.$refs['welcome-form-email-builder'].checkChangeInContent();
        if (changeInContent) {
          // open confirmation email chnage lost modal
          this.$refs[`step3-confirmation-email-change-lost`].open();
        } else {
          this.closeBefreeBuilderSlider();
        }
        this.welcome_template_id = null;
      } else if (option === 'template') {
        this.confirmMessage = this.$t('frontend_groovemail_befree_email_builder_change_template_warning');

        // open confirmation chnage tamplate lost data modal
        this.$refs[`step3-confirmation-email-change-lost`].open();
      }
    },

    // confirm email chnage lost modal
    emailChangeLostConfirm() {
      if(this.confirmTarget === 'email') {
        this.closeBefreeBuilderSlider();
      } else if (this.confirmTarget === 'template') {
        this.closeBefreeBuilderSlider();
        this.initializeEmailContentData();
        this.$emit('fetchTemplates', 2);
      }

      // reset the confirmMessage and confirmTarget
      this.confirmTarget = '';
      this.confirmMessage = '';
    },

    // open confirmation editor type
    openEditorTypeConfirmation(newEmailType, oldEmailType) {
      // set new and old email type
      this.newEmailType = newEmailType;
      this.oldEmailType = oldEmailType;

      // open confirmation editor type
      this.$refs[`step3-confirmation-editor-type`].open();
    },

    // confirm editor type chnage
    confirmEditorTypeChange() {
      this.initializeEmailContentData();
      if ((this.newEmailType === 1 || this.newEmailType === 2)) {
        this.$emit('saveOnEmailContentReset');
      }

      this.showEmailComposers = true;

      // reset new and old email type
      this.newEmailType = null;
      this.oldEmailType = null;
    },

    // close confirmation editor type
    closeConfirmEditorTypeChange() {
      // reset new and old email type
      this.localModel.welcome_editor_type = this.oldEmailType;
      this.editorTypeChangedInternally = true;

      this.showEmailComposers = true;


      // reset new and old email type
      this.newEmailType = null;
      this.oldEmailType = null;
    },

    openBefreeBuilderSlider() {
      this.$root.$emit('expand-slider', 'generic-email-builder-modal-welcome');
    },

    closeBefreeBuilderSlider() {
      this.$root.$emit('collapse-slider', 'generic-email-builder-modal-welcome');
    },

    async openWelcomePreview() {
      this.$emit(
        'openWelcomePreview',
        {
          previewContent: this.localModel.welcome_msg_content,
          contentType: this.localModel.welcome_msg_option,
        },
        true,
        (this.localModel.welcome_editor_type === 3 || this.localModel.welcome_email_id)
      );
    },

    openEmailBuilder() {
      if (this.localModel.welcome_email_id || this.localModel.welcome_msg_content) {
        this.openBefreeBuilderSlider();
      } else {
        this.$emit('fetchTemplates', 2);
      }
    },

    assignTemplateDataInBuilder(templateId, templateType) {
      if (templateId) {
        this.emailTemplateId = templateId;
      }
      this.templateType = templateType || 'template';
      this.openBefreeBuilderSlider();
    },

    saveContentWithMeta(saveAs, emailJson, htmlData, emailTemplateMeta) {
      if (saveAs === 1 || saveAs === 3) {
        this.saveEmailContent(emailJson, htmlData, emailTemplateMeta, saveAs);
      } else if (saveAs === 2) {
        this.saveTemplateContent(emailJson, htmlData, emailTemplateMeta);
      }
    },

    async saveEmailContent(emailJson, htmlData, emailTemplateMeta, saveAs) {
      this.localModel.welcome_msg_content = htmlData;
      this.localModel.welcome_email_sections = emailJson;
      if (emailTemplateMeta) {
        this.localModel.welcome_template_name = emailTemplateMeta.templateName;
        this.localModel.welcome_template_folder = emailTemplateMeta.templateFolder;
      }
      if (this.localModel.welcome_email_id) {
        try {
          this.showLoader();
          const emailPayload = this.fetchEmailPayload(emailJson, htmlData, emailTemplateMeta);
          await axios.put(
            `/email/template/${this.localModel.welcome_email_id}`, emailPayload
          );
          if (saveAs === 3) {
            this.closeBefreeBuilderSlider();
            this.welcome_template_id = null;
          }
          this.hideLoader();
        } catch (e) {
          this.showErrorMessage('frontend_groovemail_email_builder_templates_saving_retry');
          this.hideLoader();
        }
      } else {
        try {
          this.showLoader();
          const emailPayload = this.fetchEmailPayload(emailJson, htmlData, emailTemplateMeta);
          const emailSaveRes = await axios.post(
            `/email/template/create`, emailPayload
          );
          this.localModel.welcome_email_id = emailSaveRes?.data?.id ? emailSaveRes?.data?.id : null;
          this.emailTemplateId = this.localModel.welcome_email_id;
          this.templateType = 'user-email';
          if (saveAs === 3) {
            this.closeBefreeBuilderSlider();
            this.welcome_template_id = null;
          }
          this.hideLoader();
        } catch (e) {
          this.showErrorMessage('frontend_groovemail_email_builder_templates_saving_retry');
          this.hideLoader();
        }
      }
    },

    fetchEmailPayload(jsonData, htmlData, templateMeta) {
      return {
        message: htmlData,
        template_name: templateMeta.templateName,
        category_id: templateMeta.templateFolder,
        emailContentSections: jsonData,
        screenshot_image: templateMeta.screenshot ? templateMeta.screenshot : '',
      };
    },

    async saveTemplateContent(emailJson, htmlData, emailTemplateMeta) {
      if (emailTemplateMeta.templateId) {
        await this.updateTemplateContent(emailJson, htmlData, emailTemplateMeta);
      } else {
        await this.saveNewTemplateContent(emailJson, htmlData, emailTemplateMeta);
      }
    },

    fetchTemplatePayload(jsonData, htmlData, templateMeta) {
      return {
        template_name: templateMeta.templateName,
        category_id: templateMeta.templateFolder,
        emailContentSections: jsonData,
        message: htmlData,
        screenshot_image: templateMeta.screenshot ? templateMeta.screenshot : '',
      };
    },

    async saveNewTemplateContent(jsonData, htmlData, templateMeta) {
      try {
        this.showLoader();
        const templatePayload = this.fetchTemplatePayload(jsonData, htmlData, templateMeta);
        const templateSaveRes = await axios.post(
          `/user/template/create`, templatePayload
        );
        this.welcome_template_id = templateSaveRes?.data?.id ? templateSaveRes?.data?.id : null;
        this.hideLoader();
      } catch (e) {
        this.showErrorMessage('frontend_groovemail_email_builder_templates_saving_retry');
        this.hideLoader();
      }
    },

    async updateTemplateContent(jsonData, htmlData, templateMeta) {
      try {
        this.showLoader();
        const templatePayload = this.fetchTemplatePayload(jsonData, htmlData, templateMeta);
        const templateSaveRes = await axios.put(
          `/user/template/${this.welcome_template_id}`, templatePayload
        );
        this.welcome_template_id = templateSaveRes?.data?.id ? templateSaveRes?.data?.id : null;
        this.hideLoader();
      } catch (e) {
        this.showErrorMessage('frontend_groovemail_email_builder_templates_saving_retry');
        this.hideLoader();
      }
    },

    initializeEmailContentData() {
      this.localModel.welcome_msg_content = '';
      this.localModel.welcome_email_sections = '';
      this.localModel.welcome_email_id = null;
      this.localModel.welcome_template_name = '';
      this.localModel.welcome_template_folder = null;
    },

    closeSubjectVariables() {
      this.showSubjectDropdown = false;
    },

    openSubjectVariables() {
      this.showSubjectDropdown = true;
    },

    closeSmsVariables() {
      this.showSmsVariableDropdown = false;
    },

    openSmsVariables() {
      this.showSmsVariableDropdown = true;
    },

    // subject variables search function
    searchQueryChangedSmsVariable(searchQuery) {
      if (searchQuery) {
        this.dynamic_variables_sms_content_subject = this.dynamic_variables_sms_subject.filter(
          (ele) => ele.label.toLowerCase().includes(searchQuery.toLowerCase())
        );
      } else {
        this.dynamic_variables_sms_content_subject = this.dynamic_variables_sms_subject;
      }
    },

    updateEmailSender(data) {
      if (data && data === 'createSenderOption1') {
        this.localModel.welcome_sender_id = null;
        this.openCreateEmailSenderModal();
      }
    },

    updateSmsSender(data) {
      if (data && data === 'createSenderOption1') {
        this.localModel.welcome_sender_id = null;
        this.openCreateSmsSenderModal();
      }
    },

    async fetchCustomFieldVariable() {
      try {
        const customFieldsData = await axios.post('/customfield-variables');
        customFieldsData.data.forEach(fieldData => {
          this.dynamic_variables_sms_subject.push(fieldData);
          const mergeTagData = fieldData;
          mergeTagData.name = mergeTagData.label;
          this.builderVariables.push(mergeTagData);
        });
      } catch (e) {
        console.log(e);
      }
    },
    
    async emailSenderCreated(senderData, setSender) {
      if (this.verifiedSendersRequired) {
        const { data } = await axios.get('/sender', {
          params: { verify: 1 },
        });
        if (data.length > 0) {
          this.senders = data;
        }
      } else {
        const { data } = await axios.get('/sender');
        if (data.length > 0) {
          this.senders = data;
        }
      }
      if (!this.verifiedSendersRequired && setSender && senderData && senderData.id) {
        this.localModel.welcome_sender_id = senderData.id;
      }
    },

    openCreateEmailSenderModal() {
      this.$emit('openCreateEmailSenderModal', { type: 2 });
    },

    async smsSenderCreated(senderData, setSender) {
      const fetchedSenders = await axios.get('/sms-sender');
      this.smsSenders = fetchedSenders.data;
      if (setSender && senderData && senderData.id) {
        this.localModel.welcome_sender_id = senderData.id;
      }
    },

    openCreateSmsSenderModal() {
      this.$emit('openCreateSmsSenderModal', { type: 2 });
    },

    async checkPhonePresent() {
      if (
        this.localModel &&
        this.localModel.form_fields &&
        this.localModel.form_fields.length
      ) {
        this.localModel.form_fields.forEach((ele) => {
          if (ele.name === 'phone_number') {
            this.formConatinsPhone = true;
          }
        });
      }
      let smsOption;
      if (this.options && this.options.length) {
        smsOption = this.options.findIndex((ele) => {
          return ele.id === 'sms';
        });
      }
      if (this.formConatinsPhone) {
        this.options[smsOption].sms_not_allowed = false;
      } else {
        this.options[smsOption].sms_not_allowed = true;
      }
    },

    async fetchSendersStatus() {
      const sendersData = await axios.get('/email-sms-sender');
      if (sendersData.data.admin_setting) {
        this.verifiedSendersRequired = true;
      } else {
        this.verifiedSendersRequired = false;
      }
    },

    async fetchSendersData() {
      if (this.localModel.welcome_msg_option === 'email') {
        await this.fetchsenders();
      } else if (this.localModel.welcome_msg_option === 'sms') {
        await this.fetchSmsSenders();
      } else {
        this.localModel.welcome_sender_id = null;
      }
    },

    // Sets the default sender ID if the current sender ID is invalid
    setDefaultSenderIdIfInvalid(senderTarget) {
      if (
        (this.$route.name === 'NewForm' &&
          (!this.localModel.welcome_sender_id ||
            this.localModel.welcome_sender_id === '-1')) ||
        (this.$route.name === 'EditForm' &&
          (!this.localModel.welcome_sender_id ||
            this.localModel.welcome_sender_id === '-1'))
      ) {
        for (let i = 0; i < senderTarget.length; i += 1) {
          if (senderTarget[i].is_default === 1) {
            this.localModel.welcome_sender_id = senderTarget[i].id;
          }
        }
      }

      // If the current sender ID is invalid, sets it to null
      if (this.localModel.welcome_sender_id) {
        const sender = senderTarget.find((ele) => {
          return ele.id === this.localModel.welcome_sender_id;
        });
        if (!sender) {
          this.localModel.welcome_sender_id = null;
        }
      }
    },

    // Fetches SMS sender data from the '/sms-sender' endpoint using an HTTP GET request.
    async fetchSmsSenders() {
      const integerationData = await axios.get('/sms-sender');

      // If the response contains data, sets the 'smsSenders' property to the data.
      if (
        integerationData &&
        integerationData.data &&
        integerationData.data.length
      ) {
        this.smsSenders = integerationData.data;
      }


      // Sets the default sender ID if the current sender ID is invalid
      // if (
      //   (this.$route.name === 'NewForm' &&
      //     (!this.localModel.welcome_sender_id ||
      //       this.localModel.welcome_sender_id === '-1')) ||
      //   (this.$route.name === 'EditForm' &&
      //     (!this.localModel.welcome_sender_id ||
      //       this.localModel.welcome_sender_id === '-1'))
      // ) {
      //   for (let i = 0; i < this.smsSenders.length; i++) {
      //     if (this.smsSenders[i].is_default === 1) {
      //       this.localModel.welcome_sender_id = this.smsSenders[i].id;
      //     }
      //   }
      // }
      // if (this.localModel.welcome_sender_id) {
      //   const sender = this.smsSenders.find((ele) => {
      //     return ele.id === this.localModel.welcome_sender_id;
      //   });
      //   if (!sender) {
      //     this.localModel.welcome_sender_id = null;
      //   }
      // }

      this.setDefaultSenderIdIfInvalid(this.smsSenders);
    },

    // Fetches email sender data from the '/sender' endpoint using an HTTP GET request.
    async fetchsenders() {
      if (this.verifiedSendersRequired) {
        const { data } = await axios.get('/sender', {
          params: { verify: 1 },
        });
        if (data.length > 0) {
          this.senders = data;
        }
      } else {
        const { data } = await axios.get('/sender');
        if (data.length > 0) {
          this.senders = data;
        }
      }
      
      // Sets the default sender ID if the current sender ID is invalid
      // if (
      //   (this.$route.name === 'NewForm' &&
      //     (!this.localModel.welcome_sender_id ||
      //       this.localModel.welcome_sender_id === '-1')) ||
      //   (this.$route.name === 'EditForm' &&
      //     (!this.localModel.welcome_sender_id ||
      //       this.localModel.welcome_sender_id === '-1'))
      // ) {
      //   for (let i = 0; i < this.senders.length; i++) {
      //     if (this.senders[i].is_default === 1) {
      //       this.localModel.welcome_sender_id = this.senders[i].id;
      //     }
      //   }
      // }
      // if (this.localModel.welcome_sender_id) {
      //   const sender = this.senders.find((ele) => {
      //     return ele.id === this.localModel.welcome_sender_id;
      //   });
      //   if (!sender) {
      //     this.localModel.welcome_sender_id = null;
      //   }
      // }

      this.setDefaultSenderIdIfInvalid(this.senders);
    },

    validateBeforeSubmit() {
      let isValid = false;

      // If the welcome message option is 'email', validates the email content
      if (this.localModel.welcome_msg_option === 'email') {
        isValid =
          !this.$v.localModel.$invalid &&
          this.localModel.welcome_sender_id &&
          this.localModel.welcome_sender_id !== '-1' &&
          this.localModel.welcome_msg_content &&
          ((this.localModel.welcome_editor_type === 1 || this.localModel.welcome_editor_type === 2) ||
            this.localModel.welcome_email_sections);
      } else if (this.localModel.welcome_msg_option === 'sms') { // If the welcome message option is 'sms', validates the SMS content
        isValid =
          this.localModel.welcome_sender_id &&
          this.localModel.welcome_sender_id !== '-1' &&
          this.localModel.welcome_msg_sms_content;
      } else {
        isValid = true;
      }

      // If the form is valid, validates the recipients
      return isValid;
    },

    validate() {
      if (
        this.localModel.welcome_msg_option === 'email' &&
        (this.localModel.welcome_editor_type === 1 || this.localModel.welcome_editor_type === 2)
      ) {
        this.validateEmailContent();
      }
      this.$v.$touch();
      if (this.localModel.welcome_msg_option === 'email') {
        this.isEmailContentSubmitted = true;
      } else if (this.localModel.welcome_msg_option === 'sms') {
        this.isSmsContentSubmitted = true;
      }
      return this.validateBeforeSubmit();
    },

    // Set the variable in the content for the SMS
    copySmsvariable(option) {
      // target element to insert variable
      const content = document.getElementById('sms-text-area');

      let position;
      
      if (content && content.selectionStart != null && content.selectionStart !== undefined) {
        position = content.selectionStart;
      } else {
        return false;
      }

      this.localModel.welcome_msg_sms_content = [
        this.localModel.welcome_msg_sms_content.slice(0, position),
        option.value,
        this.localModel.welcome_msg_sms_content.slice(position),
      ].join('');

      content.focus();

      content.setSelectionRange(
        position + option.value.length,
        position + option.value.length
      );
      
      this.showSmsVariableDropdown = false;
      this.smsVariableSearch = '';
      this.dynamic_variables_sms_content_subject = this.dynamic_variables_sms_subject;
    },

    copyVariableInSubject(option) {
      if (!this.localModel.welcome_msg_subject) {
        this.localModel.welcome_msg_subject = '';
      }
      this.localModel.welcome_msg_subject += `${option.value}`;
      this.showSubjectDropdown = false;
      this.emailSubjectVariableSearch = '';
      this.dynamic_variables_sms_content_subject = this.dynamic_variables_sms_subject;
    },

    async proceeedSendingTestEmail() {
      if (
        this.localModel.welcome_msg_option === 'email' &&
        this.localModel.welcome_sender_id &&
        this.localModel.welcome_sender_id !== -1 &&
        this.localModel.welcome_msg_content &&
        !this.$v.localModel.welcome_msg_subject.$invalid
      ) {
        await this.$refs.sendWelcomeTestMail.sendTestEmail(
          this.localModel.welcome_sender_id,
          this.localModel.welcome_msg_subject,
          this.localModel.welcome_msg_previewtext,
          this.localModel.welcome_msg_content
        );
      } else if (!this.localModel.welcome_sender_id || this.localModel.welcome_sender_id === -1) {
        this.showErrorMessage('frontend_groovemail_send_test_email_sender_id_error');
      } else if (this.$v.localModel.welcome_msg_subject.$invalid) {
        this.showErrorMessage('frontend_groovemail_send_test_email_subject_error');
      } else if (!this.localModel.welcome_msg_content) {
        this.showErrorMessage('frontend_groovemail_send_test_email_content_error');
      } else {
        this.showErrorMessage('frontend_error');
      }
    },

    showErrorMessage(errorMessage) {
      // hide the loader
      this.hideLoader();

      // show the error message
      showAlert({
        iconType: 'alert-error',
        html : `
          <style>.swal2-confirm { color: ${themeConfig.appPrimaryColorInvert} !important; }</style>
          <p class="text-sm text-deep-dark"> ${ this.$t(errorMessage) }</p>
        `,
        showCancelButton: false,
        showCloseButton: true,
        confirmButtonColor: themeConfig.appPrimaryColor,
        confirmButtonText: this.$t(
          'frontend_close'
        ),
      });
    },

    async showSuccessMessage(title, subTitle) {
      await showSuccessDialog(title, subTitle);
    },

    recipientSelected(node) {
      if (node.id !== 'createRecipientOption1') {
        this.localModel.recipients.push({
          id: node.id,
          email: node.email,
        });
      } else {
        this.$root.$emit('modal-open', 'add-recipient-modal');
      }
    },

    recipientRemoved(node) {
      const recipientIndex = this.localModel.recipients.findIndex((ele) => {
        return ele.id === node.id;
      });
      this.localModel.recipients.splice(recipientIndex, 1);
    },

    closeRecipientModal() {
      this.$root.$emit('modal-close', 'add-recipient-modal');
    },

    addRecipientModalClosed() {
      this.newRecipientData.name='';
      this.newRecipientData.email = '';
      this.newRecipientData.otp = '';
      this.$v.newRecipientData.name.$reset(); 
      this.$v.newRecipientData.email.$reset();
      this.$v.newRecipientData.otp.$reset();
      if(this.$refs?.addNewRecipientModal?.otpFieldActive) {
        this.$refs.addNewRecipientModal.otpFieldActive = false;
      }
    },

    async fetchRecipients() {
      const recipients = await axios.get('/get-verified-emails');
      if(recipients?.data) {
        this.recipients = recipients?.data.length ? recipients.data : [];
      }
    },

    async refreshRecipients() {
      try{
        addSpinningClass('recipients');
        await this.fetchRecipients();
        removeSpinningClass('recipients');
        this.disableRefreshRecipientIcon = true;
        this.startRefreshTimeout();
      }catch(e) {
        removeSpinningClass('recipients');
      }
    },

    startRefreshTimeout() {
      this.refreshTimeoutId = setTimeout(() => {
        this.disableRefreshRecipientIcon = false;
      }, 60000);
    },

    clearRefreshTimeout() {
      clearTimeout(this.refreshTimeoutId);
    },
  },

  beforeDestroy() {
    this.clearRefreshTimeout();
  },

  computed: {
    builderVariables() {
      return [
        {
          name: this.$t('frontend_groovemail_contacts_step2_first_name'),
          label: this.$t('frontend_groovemail_contacts_step2_first_name'),
          value: '{first_name}',
        },
        {
          name: this.$t('frontend_groovemail_contacts_step2_last_name'),
          label: this.$t('frontend_groovemail_contacts_step2_last_name'),
          value: '{last_name}',
        },
        {
          name: this.$t('frontend_groovemail_email_show_email'),
          label: this.$t('frontend_groovemail_email_show_email'),
          value: '{email}',
        },
        {
          name: this.$t('frontend_groovemail_email_show_phone'),
          label: this.$t('frontend_groovemail_email_show_phone'),
          value: '{phone_number}',
        },
        {
          name: this.$t('frontend_groovemail_mail_lead_index_company'),
          label: this.$t('frontend_groovemail_mail_lead_index_company'),
          value: '{company}',
        },
        {
          name: this.$t('frontend_groovemail_mail_lead_index_adress1'),
          label: this.$t('frontend_groovemail_mail_lead_index_adress1'),
          value: '{address_1}',
        },
        {
          name: this.$t('frontend_groovemail_mail_lead_index_adress2'),
          label: this.$t('frontend_groovemail_mail_lead_index_adress2'),
          value: '{address_2}',
        },
        {
          name: this.$t('frontend_groovemail_mail_lead_index_city'),
          label: this.$t('frontend_groovemail_mail_lead_index_city'),
          value: '{city}',
        },
        {
          name: this.$t('frontend_groovemail_mail_lead_index_state'),
          label: this.$t('frontend_groovemail_mail_lead_index_state'),
          value: '{state}',
        },
        {
          name: this.$t('frontend_groovemail_mail_lead_index_country'),
          label: this.$t('frontend_groovemail_mail_lead_index_country'),
          value: '{country}',
        },
        {
          name: this.$t('frontend_groovemail_mail_lead_index_zipcode'),
          label: this.$t('frontend_groovemail_mail_lead_index_zipcode'),
          value: '{zip}',
        },
        {
          name: this.$t('frontend_groovemail_mail_lead_index_url'),
          label: this.$t('frontend_groovemail_mail_lead_index_url'),
          value: '{url}',
        },
        {
          name: this.$t('frontend_groovemail_mail_lead_index_taxnumber'),
          label: this.$t('frontend_groovemail_mail_lead_index_taxnumber'),
          value: '{tax_number}',
        },
        {
          name: this.$t('frontend_groovemail_mail_lead_index_billing'),
          label: this.$t('frontend_groovemail_mail_lead_index_billing'),
          value: '{billing_address}',
        },
        {
          name: this.$t('frontend_groovemail_contacts_step2_ip_address'),
          label: this.$t('frontend_groovemail_contacts_step2_ip_address'),
          value: '{ip_address}',
        },
      ];
    },

    dynamic_variables_sms_subject() {
      return [
        {
          label: this.$t('frontend_groovemail_contacts_step2_first_name'),
          value: '{first_name}',
        },
        {
          label: this.$t('frontend_groovemail_contacts_step2_last_name'),
          value: '{last_name}',
        },
        {
          label: this.$t('frontend_groovemail_email_show_email'),
          value: '{email}',
        },
        {
          label: this.$t('frontend_groovemail_email_show_phone'),
          value: '{phone_number}',
        },
        {
          label: this.$t('frontend_groovemail_mail_lead_index_company'),
          value: '{company}',
        },
        {
          label: this.$t('frontend_groovemail_mail_lead_index_adress1'),
          value: '{address_1}',
        },
        {
          label: this.$t('frontend_groovemail_mail_lead_index_adress2'),
          value: '{address_2}',
        },
        {
          label: this.$t('frontend_groovemail_mail_lead_index_city'),
          value: '{city}',
        },
        {
          label: this.$t('frontend_groovemail_mail_lead_index_state'),
          value: '{state}',
        },
        {
          label: this.$t('frontend_groovemail_mail_lead_index_country'),
          value: '{country}',
        },
        {
          label: this.$t('frontend_groovemail_mail_lead_index_zipcode'),
          value: '{zip}',
        },
        {
          label: this.$t('frontend_groovemail_mail_lead_index_url'),
          value: '{url}',
        },
        {
          label: this.$t('frontend_groovemail_mail_lead_index_taxnumber'),
          value: '{tax_number}',
        },
        {
          label: this.$t('frontend_groovemail_mail_lead_index_billing'),
          value: '{billing_address}',
        },
        {
          label: this.$t('frontend_groovemail_contacts_step2_ip_address'),
          value: '{ip_address}',
        },
      ];
    },

    email_type_options() {
      return [
        {
          id: 3,
          title: this.$t(
            'frontend_groovemail_email_type_email_builder_text'
          ),
          description: this.$t(
            'frontend_groovemail_email_type_email_builder_description'
          ),
          placeholder: '',
        },
        {
          id: 1,
          title: this.$t(
            'frontend_groovemail_email_type_basic_editor_text'
          ),
          description: this.$t(
            'frontend_groovemail_email_type_basic_editor_description'
          ),
          placeholder: '',
        },
        {
          id: 2,
          title: this.$t(
            'frontend_groovemail_email_type_html_paste_text'
          ),
          description: this.$t(
            'frontend_groovemail_email_type_html_paste_description'
          ),
          placeholder: '',
        },
      ];
    },

    options() {
      return [
        // Groovemail Rollback (coming soon added)
        {
          id: 'email',
          coming_soon: this.userCanMail === 0,
          title: this.$t('frontend_groovemail_forms_step2_send_by_mail'),
          description: 'Send welcome message via Email',
        },
        {
          id: 'sms',
          coming_soon: this.userCanMail === 0,
          title: this.$t('frontend_groovemail_forms_step2_send_by_sms'),
          sms_not_allowed: false,
          description: 'Send welcome message via SMS',
        },
        // Groovemail Rollback (coming soon added)
        {
          id: 'notsend',
          title: this.$t('frontend_groovemail_forms_step3_donot_send'),
          description: 'Do not send any welcome message',
        },
      ];
    },

    //  Can mail/Upgraded user/Sandbox mode check
    userCanMail() {
      return this.getCanMail();
    },
    //  Can mail/Upgraded user/Sandbox mode check

    emailSenderList() {
      const senderList = [];
      if (this.senders && this.senders.length) {
        this.senders.forEach((element) => {
          senderList.push({ value: element.id, text: element.name });
        });
      }
      return senderList;
    },

    smsSenderList() {
      const senderList = [];
      if (this.smsSenders && this.smsSenders.length) {
        this.smsSenders.forEach((element) => {
          senderList.push({ value: element.id, text: element.gateway_name });
        });
      }
      return senderList;
    },

    getEmailMetaData() {
      return {
        emailId: this.localModel.welcome_email_id,
        templateName: this.localModel.welcome_template_name,
        templateFolder: this.localModel.welcome_template_folder,
      };
    },
    
    recipientData() {
      const recipientData = [];
      if (this.recipients && this.recipients.length) {
        this.recipients.forEach((element) => {
          recipientData.push({ id: element.id, email: element.email });
        });
      }
      const recipientIndex = recipientData.findIndex((recipientEle) => {
        return recipientEle.id === 'createRecipientOption1';
      });
      if (recipientIndex < 0) {
        recipientData.push({
          id: 'createRecipientOption1',
          email: this.$t('frontend_groovemail_form_add_new_recipient'),
        });
      }
      return recipientData;
    },
  },

  watch: {
    localModel() {
      this.$emit('updateModel', this.localModel);
    },

    'localModel.welcome_msg_option': function () {
      this.localModel.welcome_msg_content = '';
      this.localModel.welcome_email_sections = '';
      if (this.localModel.welcome_msg_option === 'email') {
        this.localModel.welcome_msg_sms_content = '';
      } else if (this.localModel.welcome_msg_option === 'sms') {
        this.localModel.welcome_msg_subject = '';
        const phoneNumberField = this.localModel.form_fields.find((ele) => ele.name === 'phone_number');
        if (!phoneNumberField.mandatory) {
          Notify.warn(
            this.$t('frontend_groovemail_forms_phone_number_field_mandatory_warning_message')
          );
        }
      } else {
        this.localModel.welcome_msg_subject = '';
        this.localModel.welcome_sender_id = null;
      }
    },

    'localModel.welcome_editor_type': async function handleEmailTypeChange(newEmailType, oldEmailType) {
      if (this.editorTypeChangedInternally) {
        this.editorTypeChangedInternally = false;
      } else if (
        (((newEmailType === 1 || newEmailType === 2) && oldEmailType === 3) ||
        newEmailType === 3) && this.localModel.welcome_msg_content
      ) {
        this.showEmailComposers = false;
        
        // open confirmation editor type
        this.openEditorTypeConfirmation(newEmailType, oldEmailType);
      } else if (newEmailType === 2 && oldEmailType === 1) {
        this.validateEmailContent();
      }
    },

    'localModel.recipients': function () {
      if (this.localModel.recipients && this.localModel.recipients.length) {
        const recipientIndex = this.localModel.recipients.findIndex((ele) => {
          return ele?.id === 'createRecipientOption1';
        });
        if (recipientIndex >= 0) {
          this.localModel.recipients.splice(recipientIndex, 1);
        }
      }
    },
  },
};
</script>

<style lang="scss">
#recipients {
  .refresh-icon-disabled {	
    cursor: pointer;
    color: #a8aaaf;	
  }	
  .tooltip-refresh-btn {	
    position: relative;	
    display: inline-block;	
  }	
  .tooltip-refresh-btn .tooltiptext {	
    font-size: 14px;
    visibility: hidden;	
    width: 110px;	
    background-color: #fff;	
    color: #000;	
    box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.2);
    text-align: center;	
    border-radius: 6px;	
    padding: 5px 0;	
    /* Position the tooltip */	
    position: absolute;	
    z-index: 1;	
    top: 50%;	
    left: 50%;
    transform: translate(20%, -50%);	
  }	
  .tooltip-refresh-btn:hover .tooltiptext {	
    visibility: visible;	
  }	
  .refresh-icon {	
    cursor: pointer;	
    transition: transform 0.5s ease-in-out;
  }	

  .refresh-icon.spin {	
    animation: spin 0.5s infinite linear;	
  }	

  @keyframes spin {	
    from {	
      transform: rotate(0deg);	
    }	
    to {	
      transform: rotate(360deg);	
    }	
  }
}
</style>
